import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { ErrorModal } from '../components/ui/ErrorModal';
import { BrandSpinner } from '../components/common/LoadingSpinner';
import logo3 from '../assets/logo3.svg';
import { 
  CheckCircle,
  Clock,
  AlertTriangle,
  ArrowLeft,
  RefreshCw,
  Shield,
  FileText,
  Eye,
  Loader2,
  Sparkles
} from 'lucide-react';
import { getToken } from '../utils/authToken';
import { APP_CONFIG } from '../config/app-config';
import {
  buildRejectionTitle,
  deriveFailedDocumentLabel,
  deriveIdentityApproved,
  findRejectedPoaRecord,
} from '../utils/kycDocumentGating';

type KYCStatus = 'not_started' | 'documents_uploaded' | 'under_review' | 'verified' | 'rejected' | 'ussd_basic';

interface StatusStage {
  id: string;
  title: string;
  description: string;
  status: 'completed' | 'current' | 'pending';
  icon: React.ReactNode;
}

interface TierConfig {
  label: string;
  description: string;
  singleTransactionLimit: number;
  dailyLimit: number;
  monthlyLimit: number;
  maxBalance: number;
  canSendMoney: boolean;
  canWithdrawCash: boolean;
  canPurchaseVAS: boolean;
  canReceiveDeposits: boolean;
  canTransferInternational: boolean;
}

const KYC_TIER_LIMITS: Record<number, TierConfig> = {
  0: {
    label: 'USSD Basic',
    description: 'Name + ID/passport format-validated (no document)',
    singleTransactionLimit: 1000,
    dailyLimit: 3000,
    monthlyLimit: 5000,
    maxBalance: 3000,
    canSendMoney: false,
    canWithdrawCash: false,
    canPurchaseVAS: true,
    canReceiveDeposits: false,
    canTransferInternational: false,
  },
  1: {
    label: 'ID Verified',
    description: 'ID document uploaded and OCR-validated (Exemption 17)',
    singleTransactionLimit: 5000,
    dailyLimit: 5000,
    monthlyLimit: 25000,
    maxBalance: 25000,
    canSendMoney: true,
    canWithdrawCash: true,
    canPurchaseVAS: true,
    canReceiveDeposits: true,
    canTransferInternational: false,
  },
  2: {
    label: 'Fully Verified',
    description: 'ID document + proof of address verified (Full FICA CDD)',
    singleTransactionLimit: 25000,
    dailyLimit: 50000,
    monthlyLimit: 100000,
    maxBalance: 100000,
    canSendMoney: true,
    canWithdrawCash: true,
    canPurchaseVAS: true,
    canReceiveDeposits: true,
    canTransferInternational: true,
  },
};

function getTierLimits(tier: number | null | undefined): TierConfig {
  const t = tier === null || tier === undefined ? 0 : Number(tier);
  return KYC_TIER_LIMITS[t] || KYC_TIER_LIMITS[0];
}

function formatRand(amount: number): string {
  return `R${amount.toLocaleString('en-ZA')}`;
}

export function KYCStatusPage() {
  const { user, refreshUserStatus } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { fromStep?: number; submittedDoc?: 'identity' | 'address'; verifying?: boolean } | null;

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [estimatedTime, setEstimatedTime] = useState('2-5 minutes');

  const [serverKycStatus, setServerKycStatus] = useState<KYCStatus | null>(null);
  const [serverKycTier, setServerKycTier] = useState<number | null>(null);
  const [serverKycRecords, setServerKycRecords] = useState<any[]>([]);
  const [serverHasPendingDoc, setServerHasPendingDoc] = useState<boolean>(false);
  const [serverPendingDocType, setServerPendingDocType] = useState<string | null>(null);
  const [verifyingSeconds, setVerifyingSeconds] = useState(0);

  // Success Modal for Tier 1 (ID) and Tier 2 (POA) verified outcomes
  const [successModal, setSuccessModal] = useState<{
    open: boolean;
    title: string;
    message: string;
    tier: number;
    closeLabel?: string;
  }>({ open: false, title: '', message: '', tier: 1 });

  const [rejectionModal, setRejectionModal] = useState<{
    open: boolean;
    title: string;
    reason: string;
    tips: string[];
    failedDocumentLabel: 'Identity document' | 'Proof of address';
  }>({ open: false, title: '', reason: '', tips: [], failedDocumentLabel: 'Identity document' });

  // POA rejection notice for Tier-1-verified users whose proof of address was
  // rejected (2026-08-18). The backend keeps kycStatus='verified' (Tier 1
  // intact) when only the POA fails, so the hard rejection modal above (which
  // triggers on kycStatus==='rejected') does not fire. This notice surfaces
  // the POA failure reason from the per-document kycRecords array so the user
  // knows to re-upload a proof of address — without blocking them (they ARE
  // Tier 1 verified and can use the wallet at Tier 1 limits).
  const [poaRejectionNotice, setPoaRejectionNotice] = useState<{
    open: boolean;
    title: string;
    reason: string;
    tips: string[];
  }>({ open: false, title: '', reason: '', tips: [] });

  const initialStatusRef = useRef<KYCStatus>(user?.kycStatus || 'not_started');
  const initialTierRef = useRef<number | null>(user?.kycTier ?? null);
  const prevTierRef = useRef<number | null>(user?.kycTier ?? null);
  const successModalShownRef = useRef<boolean>(false);
  const poaNoticeDismissedRef = useRef(false);
  const pollBackoffRef = useRef(2_000);
  const pollStartRef = useRef<number | null>(null);
  const [showStillWorking, setShowStillWorking] = useState(false);

  const buildRejectionModalState = (data: {
    kycStatus?: string;
    kycTier?: number | null;
    kycRecord?: {
      rejectionReason?: string | null;
      documentType?: string;
      userFeedback?: {
        title?: string;
        message?: string;
        tips?: string[];
        documentType?: string;
      } | null;
    } | null;
  }) => {
    const feedback = data.kycRecord?.userFeedback;
    const failedDocumentLabel = deriveFailedDocumentLabel({
      tier: data.kycTier,
      kycStatus: data.kycStatus,
      documentType: data.kycRecord?.documentType,
      rejectionReason: data.kycRecord?.rejectionReason,
      userFeedback: feedback,
    });

    return {
      open: true,
      title: buildRejectionTitle(failedDocumentLabel, feedback?.title),
      reason: feedback?.message || data.kycRecord?.rejectionReason || 'We could not verify your document.',
      tips: feedback?.tips || [],
      failedDocumentLabel,
    };
  };

  const getStatusStages = (status: KYCStatus, tier: number, isVerifyingNow?: boolean, docType?: 'identity' | 'address'): StatusStage[] => {
    const identityVerified = deriveIdentityApproved(tier);
    const poaVerified = tier >= 2;
    const poaRejected = status === 'rejected' && identityVerified;
    const poaVerifying = isVerifyingNow && docType === 'address';

    const stages: StatusStage[] = [
      {
        id: 'upload',
        title: identityVerified
          ? 'Identity Document Verified (Tier 1)'
          : (isVerifyingNow && docType === 'identity' ? 'Identity Verification In Progress' : 'Documents Uploaded'),
        description: identityVerified
          ? 'Your identity document has been verified successfully'
          : (isVerifyingNow && docType === 'identity' ? 'Our AI is actively analyzing your identity document' : 'Your identity document has been received'),
        status: identityVerified ? 'completed' : (isVerifyingNow && docType === 'identity' ? 'current' : (status === 'not_started' ? 'pending' : 'completed')),
        icon: identityVerified ? <CheckCircle className="w-5 h-5" /> : <FileText className="w-5 h-5" />
      },
      {
        id: 'review',
        title: poaVerified
          ? 'Proof of Address Verified (Tier 2)'
          : poaRejected
            ? 'Proof of Address Needs Attention'
            : poaVerifying
              ? 'Proof of Address AI Verification'
              : identityVerified
                ? 'Proof of Address (Tier 2)'
                : 'Under Review',
        description: poaVerified
          ? 'Your proof of address has been verified successfully'
          : poaRejected
            ? 'Your proof of address could not be verified and needs to be re-uploaded'
            : poaVerifying
              ? 'Our AI is actively analyzing your proof of address'
              : identityVerified
                ? 'Upload proof of address to unlock higher limits and international transfers'
                : 'Our team is verifying your identity document using secure OCR technology',
        status: poaVerified
          ? 'completed'
          : (poaVerifying || poaRejected ? 'current' :
             (!identityVerified && (status === 'under_review' || isVerifyingNow)) ? 'current' : 'pending'),
        icon: poaVerified ? <CheckCircle className="w-5 h-5" /> : <Eye className="w-5 h-5" />
      },
      {
        id: 'complete',
        title: status === 'rejected' && !identityVerified
          ? 'Verification Failed'
          : poaVerified
            ? 'Full Verification Complete (Tier 2)'
            : identityVerified
              ? 'Tier 1 Verification Complete'
              : 'Verification Complete',
        description: status === 'rejected' && !identityVerified
          ? (rejectionModal.reason || 'Some documents need to be re-submitted')
          : poaVerified
            ? 'Your identity and proof of address are fully verified'
            : identityVerified
              ? 'Your identity has been verified successfully (Exemption 17)'
              : 'Your identity has been verified successfully',
        status: (poaVerified || (identityVerified && !poaVerifying))
          ? 'completed'
          : status === 'rejected' ? 'current' : 'pending',
        icon: status === 'rejected' && !identityVerified ?
          <AlertTriangle className="w-5 h-5" /> :
          <CheckCircle className="w-5 h-5" />
      }
    ];

    return stages;
  };

  const handleRefreshStatus = async () => {
    setIsRefreshing(true);
    try {
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APP_CONFIG.API.baseUrl}/api/v1/kyc/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 429) {
        pollBackoffRef.current = Math.min(pollBackoffRef.current * 2, 30_000);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch KYC status');
      }

      pollBackoffRef.current = 2_000;

      const data = await response.json();
      const newStatus = data.kycStatus as KYCStatus;
      const newTier = data.kycTier != null ? Number(data.kycTier) : null;

      setServerKycStatus(newStatus);
      if (newTier != null) {
        setServerKycTier(newTier);
      }
      setServerKycRecords(data.kycRecords || []);
      setServerHasPendingDoc(!!data.hasPendingDocument);
      setServerPendingDocType(data.pendingDocument || null);

      if (newStatus === 'verified') {
        // Tier-1-verified user whose POA was rejected: surface a non-blocking
        // POA rejection notice from the per-document kycRecords array. The
        // user is Tier 1 verified (identity approved) but their POA (Tier 2)
        // failed — tell them to re-upload without demoting their verified state.
        // Respect the dismissal ref so the notice doesn't re-open every poll.
        const rejectedPoa = findRejectedPoaRecord(data.kycRecords);
        if (rejectedPoa && newTier != null && newTier < 2 && !poaNoticeDismissedRef.current) {
          const poaFeedback = rejectedPoa.userFeedback || {};
          setPoaRejectionNotice({
            open: true,
            title: poaFeedback.title || 'Proof of Address Needs Attention',
            reason: poaFeedback.message || rejectedPoa.rejectionReason ||
              'Your proof of address could not be verified. Please upload a recent utility bill, bank statement, or municipal account.',
            tips: poaFeedback.tips || [
              'Upload a clear photo of a utility bill, bank statement, or municipal account',
              'The document must be less than 3 months old',
              'Your name and residential address must be clearly visible',
              'JPEG, PNG, or PDF supported, max 10MB',
            ],
          });
        } else if (!rejectedPoa || (newTier != null && newTier >= 2)) {
          // No rejected POA (or user reached Tier 2) — clear any stale notice.
          setPoaRejectionNotice({ open: false, title: '', reason: '', tips: [] });
        }

        // Trigger Success Modal if user was verified for Tier 1 or Tier 2!
        if (newTier != null && newTier >= 1 && !successModalShownRef.current) {
          const isTier2 = newTier >= 2;
          const cameFromStep2 = locationState?.fromStep === 2 || locationState?.submittedDoc === 'address';
          const cameFromStep1 = locationState?.fromStep === 1 || locationState?.submittedDoc === 'identity';
          const tierPromoted = prevTierRef.current != null && newTier > prevTierRef.current;
          const initialTierPromoted = initialTierRef.current != null && newTier > initialTierRef.current;
          const wasUnderReview = initialStatusRef.current === 'under_review' || initialStatusRef.current === 'documents_uploaded' || initialStatusRef.current === 'not_started';

          if (isTier2 && (cameFromStep2 || (initialTierRef.current != null && initialTierRef.current < 2) || tierPromoted || initialTierPromoted || wasUnderReview)) {
            successModalShownRef.current = true;
            setSuccessModal({
              open: true,
              tier: 2,
              title: 'Proof of Address Verified!',
              message: 'Your proof of address has been verified successfully!\n\nYour wallet has been upgraded to Tier 2 (Fully Verified) with maximum limits (R50,000/day, R100,000/month) and international transfers enabled.',
              closeLabel: 'Continue to Dashboard'
            });
          } else if (!isTier2 && (cameFromStep1 || (initialTierRef.current != null && initialTierRef.current < 1) || tierPromoted || initialTierPromoted || wasUnderReview)) {
            successModalShownRef.current = true;
            setSuccessModal({
              open: true,
              tier: 1,
              title: 'Identity Document Verified!',
              message: 'Your identity document has been verified successfully!\n\nYour wallet limits have been upgraded to Tier 1 (R5,000/day, R25,000/month). You can now send money, receive deposits, and withdraw cash.\n\nTo unlock maximum limits and international transfers, upload your proof of address.',
              closeLabel: 'Upload Proof of Address'
            });
          }
        }

        prevTierRef.current = newTier;
        if (refreshUserStatus) {
          try { await refreshUserStatus(); } catch (_) { /* no-op */ }
        }
        return;
      }

      if (newStatus === 'rejected') {
        setRejectionModal(buildRejectionModalState(data));
      }

      prevTierRef.current = newTier;
      if (refreshUserStatus) {
        await refreshUserStatus();
      }
    } catch (error) {
      console.error('Failed to refresh status:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const effectiveStatus = serverKycStatus || user?.kycStatus || 'not_started';
  const effectiveTier = serverKycTier ?? user?.kycTier ?? 0;

  const hasPendingPoa = serverPendingDocType === 'utility_bill' || serverKycRecords.some((r: any) => r.status === 'pending' && (r.documentType === 'utility_bill' || r.documentType === 'proof_of_address'));
  const hasPendingIdentity = serverPendingDocType === 'id_card' || serverKycRecords.some((r: any) => r.status === 'pending' && r.documentType !== 'utility_bill' && r.documentType !== 'proof_of_address');
  const hasPendingRecord = hasPendingPoa || hasPendingIdentity || serverHasPendingDoc;

  const isVerifying = 
    hasPendingRecord ||
    (effectiveStatus === 'under_review' && (effectiveTier < 1 || hasPendingPoa || locationState?.submittedDoc === 'address')) || 
    (effectiveStatus === 'documents_uploaded' && (effectiveTier < 1 || hasPendingPoa || locationState?.submittedDoc === 'address')) ||
    (locationState?.submittedDoc === 'address' && effectiveTier < 2 && !poaRejectionNotice.open && !successModal.open && effectiveStatus !== 'rejected') ||
    (locationState?.submittedDoc === 'identity' && effectiveTier < 1 && !successModal.open && effectiveStatus !== 'rejected');

  const verifyingDocType: 'identity' | 'address' = 
    (locationState?.submittedDoc === 'address' || hasPendingPoa || (effectiveTier >= 1 && effectiveStatus === 'under_review' && locationState?.submittedDoc !== 'identity' && !hasPendingIdentity))
      ? 'address'
      : 'identity';

  const getVerifyingSubtitle = (seconds: number, docType: 'identity' | 'address') => {
    if (docType === 'address') {
      if (seconds < 4) return 'Document received — initializing secure AI analysis…';
      if (seconds < 10) return 'AI reading document details, dates & address info…';
      if (seconds < 17) return 'Verifying FICA compliance & cross-referencing information…';
      return 'Finalizing your verification & upgrading to Tier 2…';
    } else {
      if (seconds < 4) return 'Document received — initializing secure AI analysis…';
      if (seconds < 10) return 'AI reading identity data & security features…';
      if (seconds < 17) return 'Verifying FICA compliance & SA NPR alignment…';
      return 'Finalizing your verification & activating Tier 1…';
    }
  };

  const verifyingProgressPercent = Math.min(95, Math.round(15 + (verifyingSeconds / 22) * 75));

  const getProgressValue = (status: KYCStatus, tier: number): number => {
    if (isVerifying) {
      return verifyingDocType === 'address'
        ? Math.min(96, Math.round(50 + (verifyingProgressPercent / 2)))
        : Math.min(48, Math.round(verifyingProgressPercent / 2));
    }
    switch (status) {
      case 'not_started': return 0;
      case 'ussd_basic': return 33;
      case 'documents_uploaded': return 50;
      case 'under_review': return 50;
      case 'verified': return tier >= 2 ? 100 : 66;
      case 'rejected': return tier >= 1 ? 66 : 33;
      default: return 0;
    }
  };

  const getStatusColor = (status: KYCStatus): string => {
    switch (status) {
      case 'verified': return 'text-green-600';
      case 'rejected': return 'text-red-600';
      case 'under_review': return 'text-[#2D8CCA]';
      default: return 'text-gray-600';
    }
  };

  const getStatusMessage = (status: KYCStatus, tier: number): string => {
    const identityVerified = deriveIdentityApproved(tier);

    if (isVerifying) {
      return verifyingDocType === 'address'
        ? 'We are verifying your proof of address using secure AI technology. Please wait a moment...'
        : 'We are verifying your identity document using secure AI technology. Please wait a moment...';
    }

    switch (status) {
      case 'documents_uploaded':
        return identityVerified
          ? (hasPendingPoa || locationState?.submittedDoc === 'address'
              ? 'Your proof of address has been uploaded and is queued for review.'
              : 'Your identity has been verified (Tier 1). Upload proof of address to upgrade to Tier 2.')
          : 'Your identity document has been uploaded and is queued for review.';
      case 'under_review':
        return identityVerified
          ? (hasPendingPoa || locationState?.submittedDoc === 'address'
              ? 'We are verifying your proof of address using secure OCR technology.'
              : 'Your identity has been verified (Tier 1). Upload proof of address to upgrade to Tier 2.')
          : 'Our verification team is currently reviewing your identity document.';
      case 'verified':
        if (tier >= 2) {
          return 'Congratulations! Your proof of address and identity are fully verified (Tier 2). Your maximum wallet limits are active.';
        }
        return 'Congratulations! Your identity has been verified (Tier 1). Your wallet limits have been upgraded.';
      case 'ussd_basic':
        return 'Your USSD wallet is active with basic verification. Upload documents to upgrade your limits.';
      case 'rejected':
        if (identityVerified) {
          return rejectionModal.reason
            ? `Your identity is verified. ${rejectionModal.reason}`
            : 'Your identity is verified. Please upload a recent proof of address.';
        }
        return rejectionModal.reason || 'Your identity document needs to be re-submitted for verification.';
      default:
        return 'Please upload your identity document to begin verification.';
    }
  };

  const stages = getStatusStages(effectiveStatus, effectiveTier, isVerifying, verifyingDocType);
  const progressValue = getProgressValue(effectiveStatus, effectiveTier);

  useEffect(() => {
    if (isVerifying) {
      const interval = setInterval(() => {
        setVerifyingSeconds((prev) => prev + 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setVerifyingSeconds(0);
    }
  }, [isVerifying]);

  // Fetch fresh status immediately on mount so page never relies on stale context
  useEffect(() => {
    handleRefreshStatus();
  }, []);

  // Poll while status is pending / under review / rejected, or when user just submitted
  // Step 2 (POA) and is awaiting tier promotion to Tier 2.
  const shouldPoll = 
    isVerifying ||
    (effectiveStatus === 'not_started' && effectiveTier < 1) ||
    effectiveStatus === 'rejected' ||
    (locationState?.fromStep === 2 && effectiveTier < 2 && !poaRejectionNotice.open && !successModal.open);

  useEffect(() => {
    if (shouldPoll) {
      pollStartRef.current = Date.now();
      setShowStillWorking(false);
      let timer: ReturnType<typeof setTimeout>;
      const tick = () => {
        handleRefreshStatus();
        const elapsed = Date.now() - (pollStartRef.current ?? Date.now());
        if (elapsed > 45_000) {
          setShowStillWorking(true);
        }
        const interval = isVerifying ? 1_500 : pollBackoffRef.current;
        timer = setTimeout(tick, interval);
      };
      timer = setTimeout(tick, isVerifying ? 1_500 : pollBackoffRef.current);

      return () => clearTimeout(timer);
    }
  }, [shouldPoll, effectiveStatus, effectiveTier, isVerifying]);

  return (
    <div style={{ minHeight: 'calc(100vh - 64px)' }} className="bg-gradient-to-br from-[#86BE41] to-[#2D8CCA]">
      {/* Success Modal — shown for Tier 1 (ID) and Tier 2 (POA) verified outcomes */}
      <ErrorModal
        isOpen={successModal.open && !!successModal.message.trim()}
        onClose={() => {
          const tier = successModal.tier;
          setSuccessModal({ open: false, title: '', message: '', tier: 1 });
          if (tier >= 2) {
            navigate('/dashboard');
          } else {
            navigate('/kyc/documents');
          }
        }}
        title={successModal.title || 'Verification Successful!'}
        message={successModal.message}
        type="success"
        closeLabel={successModal.closeLabel || (successModal.tier >= 2 ? 'Continue to Dashboard' : 'Upload Proof of Address')}
      />

      {/* Identity document rejection modal */}
      <ErrorModal
        isOpen={rejectionModal.open && !!rejectionModal.reason.trim()}
        onClose={() => {
          setRejectionModal({ open: false, title: '', reason: '', tips: [], failedDocumentLabel: 'Identity document' });
          navigate('/kyc/documents');
        }}
        title={rejectionModal.title || 'Verification Failed'}
        message={
          rejectionModal.failedDocumentLabel
            ? `Failed document: ${rejectionModal.failedDocumentLabel}\n\n${rejectionModal.reason}`
            : rejectionModal.reason
        }
        tips={rejectionModal.tips}
        type="warning"
        closeLabel="Try Again"
      />

      {/* POA rejection notice — Tier-1-verified user whose proof of address
          was rejected (non-blocking: user is verified at Tier 1 and can keep
          using the wallet; this just tells them their POA needs re-uploading). */}
      <ErrorModal
        isOpen={poaRejectionNotice.open && !!poaRejectionNotice.reason.trim()}
        onClose={() => {
          poaNoticeDismissedRef.current = true;
          setPoaRejectionNotice({ open: false, title: '', reason: '', tips: [] });
          navigate('/kyc/documents');
        }}
        title={poaRejectionNotice.title || 'Proof of Address Needs Attention'}
        message={`Your identity is verified (Tier 1). Your proof of address could not be verified.\n\n${poaRejectionNotice.reason}`}
        tips={poaRejectionNotice.tips}
        type="warning"
        closeLabel="Re-upload Proof of Address"
      />

      {/* Mobile Container */}
      <div className="max-w-sm mx-auto bg-gradient-to-br from-[#86BE41] to-[#2D8CCA] flex flex-col" style={{ minHeight: 'calc(100vh - 64px)' }}>
        {/* Header Section */}
        <div style={{ padding: 'var(--mobile-padding)', paddingTop: '2rem', paddingBottom: '1.5rem' }}>
          {/* Back Button */}
          <div className="mb-4">
            <button
              onClick={() => navigate(effectiveStatus === 'verified' || effectiveStatus === 'ussd_basic' ? '/dashboard' : '/kyc/documents')}
              className="inline-flex items-center text-white/90 hover:text-white transition-colors touch-target"
              style={{ 
                fontFamily: 'Montserrat, sans-serif', 
                fontSize: 'var(--mobile-font-small)',
                minHeight: 'var(--mobile-touch-target)'
              }}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              <span>{effectiveStatus === 'verified' || effectiveStatus === 'ussd_basic' ? 'Back to Dashboard' : 'Back to Documents'}</span>
            </button>
          </div>

          {/* Status Icon and Title */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 bg-white/20 backdrop-blur-sm rounded-3xl flex items-center justify-center">
              {isVerifying ? (
                <div style={{ width: 36, height: 36, animation: 'mymoolah-brand-spin 1.2s linear infinite' }}>
                  <img src={logo3} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
              ) : effectiveStatus === 'verified' || effectiveStatus === 'ussd_basic' ? (
                <CheckCircle className="w-8 h-8 text-white" />
              ) : effectiveStatus === 'rejected' ? (
                <AlertTriangle className="w-8 h-8 text-white" />
              ) : (
                <Shield className="w-8 h-8 text-white" />
              )}
            </div>
            <h1 style={{ 
              fontFamily: 'Montserrat, sans-serif', 
              fontSize: 'clamp(1.25rem, 3vw, 1.5rem)', 
              fontWeight: 'var(--font-weight-bold)', 
              color: 'white',
              marginBottom: '0.5rem'
            }}>
              {isVerifying
                ? (verifyingDocType === 'address' ? 'Verifying Proof of Address' : 'Verifying Identity Document')
                : effectiveStatus === 'verified'
                  ? (effectiveTier >= 2 ? 'Fully Verified (Tier 2)!' : 'Identity Verified (Tier 1)!')
                  : effectiveStatus === 'ussd_basic' ? 'Basic Verification'
                  : effectiveStatus === 'rejected'
                    ? (deriveIdentityApproved(effectiveTier) ? 'Proof of Address Required' : 'Action Required')
                    : 'Verification Status'}
            </h1>
            <p className="text-white/90" style={{ 
              fontFamily: 'Montserrat, sans-serif', 
              fontSize: 'var(--mobile-font-base)', 
              fontWeight: 'var(--font-weight-normal)' 
            }}>
              {getStatusMessage(effectiveStatus, effectiveTier)}
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/80" style={{ 
                fontFamily: 'Montserrat, sans-serif', 
                fontSize: 'var(--mobile-font-small)' 
              }}>
                Verification Progress
              </span>
              <span className="text-white/80" style={{ 
                fontFamily: 'Montserrat, sans-serif', 
                fontSize: 'var(--mobile-font-small)' 
              }}>
                {progressValue}%
              </span>
            </div>
            <Progress 
              value={progressValue}
              className="h-2 bg-white/20"
            />
          </div>

          {/* Refresh Button for Pending Statuses */}
          {(isVerifying || effectiveStatus !== 'verified') && (
            <div className="text-center">
              <Button
                onClick={handleRefreshStatus}
                disabled={isRefreshing || isVerifying}
                variant="outline"
                className={`text-white border-white/30 ${isVerifying ? 'bg-white/10 opacity-90 cursor-not-allowed' : 'hover:bg-white/10'}`}
                style={{ 
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: 'var(--mobile-font-small)',
                  fontWeight: 'var(--font-weight-medium)'
                }}
              >
                {isVerifying ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" />AI Verifying ({verifyingSeconds}s)...</>
                ) : isRefreshing ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" />Checking...</>
                ) : (
                  <><RefreshCw className="w-4 h-4 mr-2" />Check Status</>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Status Stages Section */}
        <div className="flex-1" style={{ padding: 'var(--mobile-padding)', paddingBottom: '2rem' }}>
          <div className="space-y-4">
            {/* Live processing indicator — prominent rotating BrandSpinner with dynamic stage messaging and progress bar */}
            {isVerifying && (
              <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-xl overflow-hidden" style={{ borderRadius: 'var(--mobile-border-radius)' }}>
                <CardContent style={{ paddingTop: '2rem', paddingBottom: '2rem', textAlign: 'center' }}>
                  <BrandSpinner
                    size={56}
                    label={verifyingDocType === 'address' ? 'Verifying Proof of Address…' : 'Verifying Identity Document…'}
                    subtitle={getVerifyingSubtitle(verifyingSeconds, verifyingDocType)}
                  />
                  <div style={{ marginTop: '1.25rem', maxWidth: 280, marginInline: 'auto' }}>
                    <div className="flex justify-between text-xs text-gray-500 mb-1.5" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      <span className="flex items-center gap-1 font-medium text-gray-600">
                        <Sparkles className="w-3.5 h-3.5 text-[#2D8CCA]" /> AI Processing
                      </span>
                      <span className="font-semibold text-[#2D8CCA]">{verifyingProgressPercent}%</span>
                    </div>
                    <div style={{ height: 6, backgroundColor: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                      <div
                        style={{
                          height: '100%',
                          width: `${verifyingProgressPercent}%`,
                          background: 'linear-gradient(90deg, #86BE41, #2D8CCA)',
                          borderRadius: 3,
                          transition: 'width 0.4s ease',
                        }}
                      />
                    </div>
                    <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '12px', color: '#6b7280', marginTop: '0.75rem' }}>
                      Please keep this screen open while we verify. This usually takes 10–20 seconds.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {showStillWorking && isVerifying && (
              <Card className="bg-amber-50 border border-amber-200 shadow-lg" style={{ borderRadius: 'var(--mobile-border-radius)' }}>
                <CardContent style={{ paddingTop: '1.25rem', paddingBottom: '1.25rem' }}>
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 'var(--mobile-font-base)', fontWeight: 'var(--font-weight-bold)', color: '#92400e' }}>
                        Still working — thank you for your patience
                      </h4>
                      <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 'var(--mobile-font-small)', fontWeight: 'var(--font-weight-normal)', color: '#92400e', marginTop: '0.25rem' }}>
                        Our AI is conducting thorough checks on your document. You can safely keep this screen open or return to the dashboard — your verification will complete in the background.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            {/* Status Timeline */}
            <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-xl" style={{ borderRadius: 'var(--mobile-border-radius)' }}>
              <CardHeader>
                <CardTitle style={{ 
                  fontFamily: 'Montserrat, sans-serif', 
                  fontSize: 'clamp(1rem, 2.5vw, 1.125rem)', 
                  fontWeight: 'var(--font-weight-bold)', 
                  color: '#1f2937' 
                }}>
                  Verification Steps
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stages.map((stage, index) => (
                    <div key={stage.id} className="flex items-start gap-4">
                      {/* Status Icon */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        stage.status === 'completed' ? 'bg-green-100 text-green-600' :
                        stage.status === 'current' ? 'bg-[#2D8CCA]/10 text-[#2D8CCA]' :
                        'bg-gray-100 text-gray-400'
                      }`}>
                        {stage.status === 'completed' && stage.id !== 'complete' ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          stage.icon
                        )}
                      </div>

                      {/* Status Content */}
                      <div className="flex-1 min-w-0">
                        <h4 style={{ 
                          fontFamily: 'Montserrat, sans-serif', 
                          fontSize: 'var(--mobile-font-base)', 
                          fontWeight: 'var(--font-weight-medium)',
                          color: stage.status === 'current' ? '#2D8CCA' : '#1f2937'
                        }}>
                          {stage.title}
                        </h4>
                        <p className="text-gray-600 mt-1" style={{ 
                          fontFamily: 'Montserrat, sans-serif', 
                          fontSize: 'var(--mobile-font-small)', 
                          fontWeight: 'var(--font-weight-normal)' 
                        }}>
                          {stage.description}
                        </p>
                        
                        {/* Current Stage Indicator */}
                        {stage.status === 'current' && (
                          <div className="mt-2 flex items-center gap-2">
                            <Clock className="w-4 h-4 text-[#2D8CCA]" />
                            <span className="text-[#2D8CCA]" style={{ 
                              fontFamily: 'Montserrat, sans-serif', 
                              fontSize: 'var(--mobile-font-small)' 
                            }}>
                              Estimated time: {estimatedTime}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Connector Line */}
                      {index < stages.length - 1 && (
                        <div className={`absolute left-5 w-0.5 h-6 mt-10 ${
                          stage.status === 'completed' ? 'bg-green-200' : 'bg-gray-200'
                        }`} />
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* KYC Tier Information - Show for verified and ussd_basic users */}
            {(effectiveStatus === 'verified' || effectiveStatus === 'ussd_basic') && (() => {
              const tier = effectiveTier;
              const limits = getTierLimits(tier);
              const nextTier = tier < 2 ? getTierLimits(tier + 1) : null;

              return (
              <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-xl" style={{ borderRadius: 'var(--mobile-border-radius)' }}>
                <CardHeader>
                  <CardTitle style={{ 
                    fontFamily: 'Montserrat, sans-serif', 
                    fontSize: 'clamp(1rem, 2.5vw, 1.125rem)', 
                    fontWeight: 'var(--font-weight-bold)', 
                    color: '#1f2937' 
                  }}>
                    Your KYC Tier & Limits
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Current Tier Display */}
                    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <Shield className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h4 style={{ 
                            fontFamily: 'Montserrat, sans-serif', 
                            fontSize: 'var(--mobile-font-base)', 
                            fontWeight: 'var(--font-weight-bold)', 
                            color: '#1f2937'
                          }}>
                            Tier {tier} - {limits.label}
                          </h4>
                          <p style={{ 
                            fontFamily: 'Montserrat, sans-serif', 
                            fontSize: 'var(--mobile-font-small)', 
                            fontWeight: 'var(--font-weight-normal)', 
                            color: '#6b7280'
                          }}>
                            {limits.description}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-green-600 font-semibold" style={{ 
                          fontFamily: 'Montserrat, sans-serif', 
                          fontSize: 'var(--mobile-font-base)'
                        }}>
                          ACTIVE
                        </div>
                      </div>
                    </div>

                    {/* Payments out */}
                    <div className="space-y-3">
                      <div>
                        <h5 style={{ 
                          fontFamily: 'Montserrat, sans-serif', 
                          fontSize: 'var(--mobile-font-small)', 
                          fontWeight: 'var(--font-weight-bold)', 
                          color: '#374151'
                        }}>
                          Payments out
                        </h5>
                        <p style={{
                          fontFamily: 'Montserrat, sans-serif',
                          fontSize: 'var(--mobile-font-small)',
                          fontWeight: 'var(--font-weight-normal)',
                          color: '#6b7280',
                          marginTop: '0.125rem'
                        }}>
                          Send money, cash withdrawal, and VAS.
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="text-blue-600 font-semibold" style={{ 
                            fontFamily: 'Montserrat, sans-serif', 
                            fontSize: 'var(--mobile-font-small)'
                          }}>
                            One payment
                          </div>
                          <div className="text-blue-800 font-bold" style={{ 
                            fontFamily: 'Montserrat, sans-serif', 
                            fontSize: 'var(--mobile-font-base)'
                          }}>
                            {formatRand(limits.singleTransactionLimit)}
                          </div>
                        </div>
                        
                        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                          <div className="text-green-600 font-semibold" style={{ 
                            fontFamily: 'Montserrat, sans-serif', 
                            fontSize: 'var(--mobile-font-small)'
                          }}>
                            Per day
                          </div>
                          <div className="text-green-800 font-bold" style={{ 
                            fontFamily: 'Montserrat, sans-serif', 
                            fontSize: 'var(--mobile-font-base)'
                          }}>
                            {formatRand(limits.dailyLimit)}
                          </div>
                        </div>

                        <div className="col-span-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                          <div className="text-purple-600 font-semibold" style={{ 
                            fontFamily: 'Montserrat, sans-serif', 
                            fontSize: 'var(--mobile-font-small)'
                          }}>
                            Per month
                          </div>
                          <div className="text-purple-800 font-bold" style={{ 
                            fontFamily: 'Montserrat, sans-serif', 
                            fontSize: 'var(--mobile-font-base)'
                          }}>
                            {formatRand(limits.monthlyLimit)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* In your wallet */}
                    <div className="space-y-3">
                      <h5 style={{ 
                        fontFamily: 'Montserrat, sans-serif', 
                        fontSize: 'var(--mobile-font-small)', 
                        fontWeight: 'var(--font-weight-bold)', 
                        color: '#374151'
                      }}>
                        In your wallet
                      </h5>
                      <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="text-amber-600 font-semibold" style={{ 
                          fontFamily: 'Montserrat, sans-serif', 
                          fontSize: 'var(--mobile-font-small)'
                        }}>
                          Highest balance
                        </div>
                        <div className="text-amber-800 font-bold" style={{ 
                          fontFamily: 'Montserrat, sans-serif', 
                          fontSize: 'var(--mobile-font-base)'
                        }}>
                          {formatRand(limits.maxBalance)}
                        </div>
                        <p style={{
                          fontFamily: 'Montserrat, sans-serif',
                          fontSize: 'var(--mobile-font-small)',
                          fontWeight: 'var(--font-weight-normal)',
                          color: '#92400e',
                          marginTop: '0.375rem'
                        }}>
                          Deposits count towards this balance. They do not use your monthly payment limit.
                        </p>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="space-y-2">
                      <h5 style={{ 
                        fontFamily: 'Montserrat, sans-serif', 
                        fontSize: 'var(--mobile-font-small)', 
                        fontWeight: 'var(--font-weight-bold)', 
                        color: '#374151'
                      }}>
                        Available Features
                      </h5>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { label: 'VAS Purchases', enabled: limits.canPurchaseVAS },
                          { label: 'Receive Deposits', enabled: limits.canReceiveDeposits },
                          { label: 'Send Money', enabled: limits.canSendMoney },
                          { label: 'Withdraw Cash', enabled: limits.canWithdrawCash },
                          { label: 'International', enabled: limits.canTransferInternational },
                        ].map((feat) => (
                          <div key={feat.label} className={`flex items-center gap-2 p-2 rounded-lg ${feat.enabled ? 'bg-green-50' : 'bg-gray-50'}`}>
                            {feat.enabled ? (
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            ) : (
                              <AlertTriangle className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            )}
                            <span style={{
                              fontFamily: 'Montserrat, sans-serif',
                              fontSize: 'var(--mobile-font-small)',
                              fontWeight: 'var(--font-weight-normal)',
                              color: feat.enabled ? '#166534' : '#9ca3af',
                            }}>
                              {feat.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Upgrade prompt — only for users below Tier 2 */}
                    {tier === 0 && (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Shield className="w-4 h-4 text-amber-600" />
                        </div>
                        <div>
                          <h6 style={{ 
                            fontFamily: 'Montserrat, sans-serif', 
                            fontSize: 'var(--mobile-font-small)', 
                            fontWeight: 'var(--font-weight-bold)', 
                            color: '#92400e'
                          }}>
                            Unlock Send, Withdraw & Higher Limits
                          </h6>
                          <p style={{ 
                            fontFamily: 'Montserrat, sans-serif', 
                            fontSize: 'var(--mobile-font-small)', 
                            fontWeight: 'var(--font-weight-normal)', 
                            color: '#92400e',
                            marginTop: '0.25rem'
                          }}>
                            To send money, receive deposits, withdraw cash and increase your limits, upload a photo of your SA ID book, SA ID card, or passport via the web app.
                          </p>
                          <Button
                            onClick={() => navigate('/kyc/documents')}
                            variant="outline"
                            size="sm"
                            className="mt-2 border-amber-300 text-amber-700 hover:bg-amber-100"
                            style={{ 
                              fontFamily: 'Montserrat, sans-serif',
                              fontSize: 'var(--mobile-font-small)',
                              fontWeight: 'var(--font-weight-medium)'
                            }}
                          >
                            Upload ID Document
                          </Button>
                        </div>
                      </div>
                    </div>
                    )}

                    {tier === 1 && !isVerifying && (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Shield className="w-4 h-4 text-amber-600" />
                        </div>
                        <div>
                          <h6 style={{ 
                            fontFamily: 'Montserrat, sans-serif', 
                            fontSize: 'var(--mobile-font-small)', 
                            fontWeight: 'var(--font-weight-bold)', 
                            color: '#92400e'
                          }}>
                            Unlock Cross-Border & Higher Limits
                          </h6>
                          <p style={{ 
                            fontFamily: 'Montserrat, sans-serif', 
                            fontSize: 'var(--mobile-font-small)', 
                            fontWeight: 'var(--font-weight-normal)', 
                            color: '#92400e',
                            marginTop: '0.25rem'
                          }}>
                            To enable international remittances and increase your transaction and balance limits, upload proof of address via the web app.
                          </p>
                          <Button
                            onClick={() => navigate('/kyc/documents')}
                            variant="outline"
                            size="sm"
                            className="mt-2 border-amber-300 text-amber-700 hover:bg-amber-100"
                            style={{ 
                              fontFamily: 'Montserrat, sans-serif',
                              fontSize: 'var(--mobile-font-small)',
                              fontWeight: 'var(--font-weight-medium)'
                            }}
                          >
                            Upload Proof of Address
                          </Button>
                        </div>
                      </div>
                    </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              );
            })()}

            {/* Action Buttons */}
            <div className="space-y-3">
              {isVerifying ? (
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="w-full border-white/60 text-white hover:bg-white/10"
                  style={{ 
                    height: 'var(--mobile-touch-target)',
                    fontFamily: 'Montserrat, sans-serif', 
                    fontSize: 'var(--mobile-font-base)', 
                    fontWeight: 'var(--font-weight-medium)',
                    borderRadius: 'var(--mobile-border-radius)'
                  }}
                >
                  Return to Dashboard
                </Button>
              ) : (effectiveStatus === 'verified' || effectiveStatus === 'ussd_basic') ? (
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-gradient-to-r from-[#86BE41] to-[#2D8CCA] hover:from-[#7AB139] hover:to-[#2680B8] text-white"
                  style={{ 
                    height: 'var(--mobile-touch-target)',
                    fontFamily: 'Montserrat, sans-serif', 
                    fontSize: 'var(--mobile-font-base)', 
                    fontWeight: 'var(--font-weight-medium)',
                    borderRadius: 'var(--mobile-border-radius)'
                  }}
                >
                  Continue to Dashboard
                </Button>
              ) : effectiveStatus === 'rejected' ? (
                <Button
                  onClick={() => navigate('/kyc/documents')}
                  className="w-full bg-gradient-to-r from-[#86BE41] to-[#2D8CCA] hover:from-[#7AB139] hover:to-[#2680B8] text-white"
                  style={{ 
                    height: 'var(--mobile-touch-target)',
                    fontFamily: 'Montserrat, sans-serif', 
                    fontSize: 'var(--mobile-font-base)', 
                    fontWeight: 'var(--font-weight-medium)',
                    borderRadius: 'var(--mobile-border-radius)'
                  }}
                >
                  {deriveIdentityApproved(effectiveTier)
                    ? 'Re-upload Proof of Address'
                    : 'Re-upload Identity Document'}
                </Button>
              ) : (
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="w-full border-[#86BE41] text-[#86BE41] hover:bg-[#86BE41] hover:text-white"
                  style={{ 
                    height: 'var(--mobile-touch-target)',
                    fontFamily: 'Montserrat, sans-serif', 
                    fontSize: 'var(--mobile-font-base)', 
                    fontWeight: 'var(--font-weight-medium)',
                    borderRadius: 'var(--mobile-border-radius)'
                  }}
                >
                  Return to Dashboard
                </Button>
              )}
            </div>

            {/* Help & Support */}
            <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-lg" style={{ borderRadius: 'var(--mobile-border-radius)' }}>
              <CardContent style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
                <div className="text-center space-y-3">
                  <Shield className="w-8 h-8 mx-auto text-[#86BE41]" />
                  <h3 style={{ 
                    fontFamily: 'Montserrat, sans-serif', 
                    fontSize: 'var(--mobile-font-base)', 
                    fontWeight: 'var(--font-weight-bold)', 
                    color: '#1f2937'
                  }}>
                    Your Data is Secure
                  </h3>
                  <p className="text-gray-600" style={{ 
                    fontFamily: 'Montserrat, sans-serif', 
                    fontSize: 'var(--mobile-font-small)', 
                    fontWeight: 'var(--font-weight-normal)' 
                  }}>
                    We use bank-grade encryption and secure OCR technology to verify your documents. Your personal information is protected and never shared without consent.
                  </p>
                  
                  {(effectiveStatus === 'documents_uploaded' || effectiveStatus === 'under_review') && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-[#2D8CCA]" style={{ 
                        fontFamily: 'Montserrat, sans-serif', 
                        fontSize: 'var(--mobile-font-small)',
                        fontWeight: 'var(--font-weight-medium)'
                      }}>
                        💡 While you wait, you can still browse your wallet and make deposits. Full transaction features will be unlocked once verification is complete.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <style>{`@keyframes slideDown { from { transform: translateY(-100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
    </div>
  );
}
